import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { BsBarChartFill, BsGrid3X3GapFill } from 'react-icons/bs'
import { ButtonCmp } from '../../../components/ButtonCmp'
import { SelectCmp } from '../../../components/SelectCmp'
import { SwitchCmp } from '../../../components/SwitchCmp'
import { appApi } from '../../../services/appApi'
import { GetMetaFinal, GetValorAcumuladoAteEsseMes, GetValorPrevisto, GetValorPrevistoAteEsseMes, GetValueCircularProgressIndicador, ObterCorPercentual } from '../../../Utils/Helper'
import { Body } from '../../Layouts/Body'
import { iIndicador, iMesPeriodo } from '../gestao'
import { IndicadorChart } from '../gestao/components/IndicadorChart'
import { AvataresParticipantes } from './components/AvataresParticipantes'
import { CardIndicador } from './components/CardIndicador'
import { GaugeIndicador } from './components/GaugeIndicador'
import { ModalGraficoIndicador } from './components/ModalGraficoIndicador'
import { TabelaIndicadorMensal } from './components/TabelaIndicadorMensal'

interface iDashboardIndicadores {
  indicadoresGlobais: iIndicador[]
  backoffice: iIndicador[]
  comercial: iIndicador[]
  desenvolvimento: iIndicador[]
  entrega: iIndicador[]
  gpsEntrega: iIndicador[]
}

const nomesMeses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

function getAcumuladoReal(ind: iIndicador, mesRef: number, ano: number): number {
  const meses = ind.periodos.find(p => p.ano === ano)?.meses ?? []
  if (ind.tipoMeta === 5) {
    const ult = meses
      .filter(m => m.numeroMes <= mesRef && m.real !== null && m.real !== undefined)
      .sort((a, b) => b.numeroMes - a.numeroMes)[0]
    return ult?.real ?? 0
  }
  return meses
    .filter(m => m.numeroMes <= mesRef)
    .reduce((soma, m) => soma + (m.real ?? 0), 0)
}

function getMesesDoAno(ind: iIndicador, ano: number): iMesPeriodo[] {
  return ind.periodos.find(p => p.ano === ano)?.meses ?? []
}

export const DashboardIndicadores: React.FC = (): JSX.Element => {
  const anoAtual = new Date().getFullYear()
  const mesAtual = new Date().getMonth() + 1
  const mesPadrao = mesAtual === 1 ? 1 : mesAtual - 1

  const [Model, setModel] = useState<iDashboardIndicadores>()
  const [IsLoading, setIsLoading] = useState(false)
  const [Mes, setMes] = useState(mesPadrao)
  const [Modo, setModo] = useState<'acumulado' | 'anual'>('acumulado')
  const [VisaoAreas, setVisaoAreas] = useState<'cards' | 'barras'>('cards')
  const [TipoGrafico, setTipoGrafico] = useState<'bar' | 'line'>('bar')
  const [MostrarTabela, setMostrarTabela] = useState(true)
  const [IndicadorSelecionado, setIndicadorSelecionado] = useState<iIndicador>()

  const mesRef = Modo === 'anual' ? 12 : Mes

  function getDashboard(): void {
    setIsLoading(true)
    appApi.get('Indicador/Dashboard')
      .then(res => setModel(res.data))
      .catch(err => console.log(err))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    getDashboard()
  }, [])

  function getMetricas(ind: iIndicador): { pctMeta: number, pctPrevisto: number, acumuladoLabel: string, previstoLabel: string, metaLabel: string } {
    const acumulado = getAcumuladoReal(ind, mesRef, anoAtual)
    const previsto = GetValorPrevisto(ind.periodos, mesRef)
    const pctMeta = ind.meta > 0
      ? (ind.metaInversa ? (acumulado === 0 ? 0 : ind.meta / acumulado) : acumulado / ind.meta) * 100
      : 0
    const pctPrevisto = GetValueCircularProgressIndicador(ind, previsto, acumulado)
    return {
      pctMeta,
      pctPrevisto,
      acumuladoLabel: GetValorAcumuladoAteEsseMes(ind, acumulado),
      previstoLabel: GetValorPrevistoAteEsseMes(ind, previsto),
      metaLabel: GetMetaFinal(ind)
    }
  }

  const areas = Model
    ? [
        { nome: 'Backoffice', indicadores: Model.backoffice },
        { nome: 'Comercial', indicadores: Model.comercial },
        { nome: 'Desenvolvimento', indicadores: Model.desenvolvimento },
        { nome: 'Entrega', indicadores: [...Model.entrega, ...Model.gpsEntrega] }
      ]
    : []

  return (
    <Body isLoading={IsLoading}>
      <Flex justifyContent={'space-between'} alignItems={'flex-end'} flexWrap={'wrap'} gap={'1rem'} mb={'1.5rem'}>
        <Box>
          <Text fontSize={'1.5rem'} fontWeight={'700'} color={'var(--Gray1)'}>Dashboard de Indicadores</Text>
          <Text fontSize={'.875rem'} color={'var(--Gray3)'}>
            {Modo === 'anual'
              ? 'Visão anual (acumulado do ano)'
              : `Acumulado até ${nomesMeses[mesRef - 1]} de ${anoAtual}`}
          </Text>
        </Box>

        <Flex alignItems={'center'} gap={'1.5rem'} flexWrap={'wrap'}>
          <SwitchCmp
            value={Modo === 'acumulado'}
            legendaEsquerda={'Anual'}
            legendaDireita={'Acumulado até o mês'}
            OnChange={(b) => setModo(b ? 'acumulado' : 'anual')}
          />
          <SelectCmp
            title={'Mês de referência'}
            value={String(Mes)}
            isDisabled={Modo === 'anual'}
            OnChange={(e) => setMes(Number(e))}
            width={'10rem'}
          >
            {nomesMeses.map((nome, i) => (
              <option key={nome} value={i + 1}>{nome}</option>
            ))}
          </SelectCmp>
        </Flex>
      </Flex>

      {Model && (
        <>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={'1rem'}>
            {Model.indicadoresGlobais.map(ind => {
              const m = getMetricas(ind)
              const ehAnual = Modo === 'anual'
              return (
                <GaugeIndicador
                  key={ind.id}
                  id={`global-${ind.id}`}
                  nome={ind.nome}
                  pctPrincipal={ehAnual ? m.pctMeta : m.pctPrevisto}
                  legendaPrincipal={ehAnual ? '% da meta anual' : `Atingido vs previsto (até ${nomesMeses[mesRef - 1]})`}
                  acumuladoLabel={m.acumuladoLabel}
                  previstoLabel={m.previstoLabel}
                  metaLabel={m.metaLabel}
                />
              )
            })}
          </SimpleGrid>

          <Flex alignItems={'center'} justifyContent={'space-between'} mt={'2.5rem'} mb={'1.25rem'} gap={'1rem'} flexWrap={'wrap'}>
            <Text fontSize={'1.25rem'} fontWeight={'700'} color={'var(--Gray1)'}>Áreas</Text>
            <Flex alignItems={'center'} gap={'1.5rem'} flexWrap={'wrap'}>
              {VisaoAreas === 'barras' && (
                <SwitchCmp
                  value={TipoGrafico === 'line'}
                  legendaEsquerda={'Colunas'}
                  legendaDireita={'Linhas'}
                  OnChange={(b) => setTipoGrafico(b ? 'line' : 'bar')}
                />
              )}
              {VisaoAreas === 'barras' && (
                <SwitchCmp
                  value={MostrarTabela}
                  legendaDireita={'Tabela de valores'}
                  OnChange={(b) => setMostrarTabela(b)}
                />
              )}
              <ButtonCmp
                VarColor={'Azul'}
                leftIcon={VisaoAreas === 'cards' ? <BsBarChartFill /> : <BsGrid3X3GapFill />}
                onClick={() => setVisaoAreas(v => v === 'cards' ? 'barras' : 'cards')}
              >
                {VisaoAreas === 'cards' ? 'Ver gráficos mensais' : 'Ver cards'}
              </ButtonCmp>
            </Flex>
          </Flex>

          {areas.map(area => (
            <Box key={area.nome} mb={'2rem'}>
              <Text fontSize={'1.05rem'} fontWeight={'700'} color={'var(--Azul)'} mb={'.75rem'}>{area.nome}</Text>

              {area.indicadores.length === 0 && (
                <Text fontSize={'.875rem'} color={'var(--Gray3)'}>Nenhum indicador nesta área.</Text>
              )}

              {VisaoAreas === 'cards' && area.indicadores.length > 0 && (
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing={'1rem'}>
                  {area.indicadores.map((ind, i) => {
                    const m = getMetricas(ind)
                    const ehAnual = Modo === 'anual'
                    return (
                      <CardIndicador
                        key={`${area.nome}-${ind.id}-${i}`}
                        indicador={ind}
                        pctPrincipal={ehAnual ? m.pctMeta : m.pctPrevisto}
                        legendaPrincipal={ehAnual ? '% da meta' : `Até ${nomesMeses[mesRef - 1]}`}
                        acumuladoLabel={m.acumuladoLabel}
                        previstoLabel={m.previstoLabel}
                        metaLabel={m.metaLabel}
                        onClick={() => setIndicadorSelecionado(ind)}
                      />
                    )
                  })}
                </SimpleGrid>
              )}

              {VisaoAreas === 'barras' && area.indicadores.length > 0 && (
                <SimpleGrid columns={1} spacing={'1rem'}>
                  {area.indicadores.map((ind, i) => {
                    const m = getMetricas(ind)
                    const indisponivel = m.acumuladoLabel === 'N/A'
                    return (
                      <Box
                        key={`${area.nome}-${ind.id}-${i}`}
                        bg={'var(--c1)'}
                        border={'1px solid var(--c5)'}
                        borderRadius={'.5rem'}
                        padding={'1rem'}
                        boxShadow={'var(--SombraBackground)'}
                      >
                        <Flex justifyContent={'space-between'} alignItems={'center'} gap={'1rem'} mb={'.5rem'} flexWrap={'wrap'}>
                          <Text fontSize={'.9rem'} fontWeight={'600'} color={'var(--Gray1)'}>{ind.nome}</Text>
                          <Flex alignItems={'center'} gap={'.9rem'} fontSize={'.8rem'} flexWrap={'wrap'}>
                            <Text color={'var(--Gray3)'}>Previsto: <Text as={'span'} fontWeight={'600'} color={'var(--Gray1)'}>{m.previstoLabel}</Text></Text>
                            <Text color={'var(--Gray3)'}>Acumulado: <Text as={'span'} fontWeight={'600'} color={'var(--Gray1)'}>{m.acumuladoLabel}</Text></Text>
                            <Text
                              fontWeight={'700'}
                              color={indisponivel ? 'var(--Gray3)' : ObterCorPercentual(m.pctPrevisto)}
                            >
                              {indisponivel ? 'N/A' : `${m.pctPrevisto.toFixed(1)}%`}
                            </Text>
                          </Flex>
                          <AvataresParticipantes responsavel={ind.responsavel} participantes={ind.participantes} />
                        </Flex>
                        <IndicadorChart
                          id={`${area.nome}-${ind.id}-${i}`}
                          data={getMesesDoAno(ind, anoAtual)}
                          indicador={ind}
                          tipo={TipoGrafico}
                          mostrarPercentual
                        />
                        {MostrarTabela && (
                          <TabelaIndicadorMensal indicador={ind} meses={getMesesDoAno(ind, anoAtual)} />
                        )}
                      </Box>
                    )
                  })}
                </SimpleGrid>
              )}
            </Box>
          ))}

          {IndicadorSelecionado && (
            <ModalGraficoIndicador
              isOpen={IndicadorSelecionado !== undefined}
              onClose={() => setIndicadorSelecionado(undefined)}
              indicador={IndicadorSelecionado}
              meses={getMesesDoAno(IndicadorSelecionado, anoAtual)}
            />
          )}
        </>
      )}
    </Body>
  )
}
